import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { accountService, ApiError, AccountDto, UpdateAccountDto } from '../services';
import { DEFAULT_LANGUAGE, safeLanguageCode } from '../utils/languageUtils';

const AccountPreferences: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [errors, setErrors] = useState<{fullName?: string, preferredLanguage?: string}>({});
  const [account, setAccount] = useState<AccountDto | null>(null);
  const [accountForm, setAccountForm] = useState({
    fullName: '',
    notificationsEnabled: true,
    preferredLanguage: DEFAULT_LANGUAGE
  });

  useEffect(() => {
    const loadAccount = async () => {
      if (!token) return;
      
      setIsLoading(true);
      try {
        const accountData = await accountService.getProfile(token);
        setAccount(accountData);
        setAccountForm({
          fullName: accountData.fullName || '',
          notificationsEnabled: accountData.notificationsEnabled,
          preferredLanguage: safeLanguageCode(accountData.preferredLanguage)
        });
      } catch (error) {
        if (error instanceof ApiError) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(t('messages.loadError'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadAccount();
  }, [token, t]);

  const validateForm = (): boolean => {
    const newErrors: {fullName?: string, preferredLanguage?: string} = {};

    if (!accountForm.preferredLanguage.trim()) {
      newErrors.preferredLanguage = t('validation.languageRequired');
    }

    if (accountForm.fullName.length > 100) {
      newErrors.fullName = t('validation.fullNameTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!token) return;

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const accountUpdate: UpdateAccountDto = {
        fullName: accountForm.fullName.trim() || undefined,
        notificationsEnabled: accountForm.notificationsEnabled,
        preferredLanguage: accountForm.preferredLanguage
      };
      
      await accountService.updateProfile(token, accountUpdate);
      setSuccessMessage(t('preferences.updateSuccess'));
      
      // Reload account data
      const updatedAccount = await accountService.getProfile(token);
      setAccount(updatedAccount);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('preferences.updateError'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) return;

    setIsDeleting(true);
    setErrorMessage('');

    try {
      await accountService.deleteAccount(token);
      logout();
      const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
      const homeRoute = t('routes.home');
      navigate(`/${currentLanguage}${homeRoute}`);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('preferences.deleteError'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            {t('preferences.accountNotFound')}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('preferences.accountTitle')}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {t('preferences.accountDescription')}
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('preferences.accountInfo')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('preferences.email')}: {account.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('preferences.emailConfirmed')}: {account.emailConfirmed ? t('common.yes') : t('common.no')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('preferences.accountCreated')}: {new Date(account.createdAt).toLocaleDateString()}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label={t('preferences.fullName')}
              placeholder={t('preferences.fullNamePlaceholder')}
              value={accountForm.fullName}
              onChange={(e) => {
                setAccountForm(prev => ({ ...prev, fullName: e.target.value }));
                if (errors.fullName) {
                  setErrors(prev => ({ ...prev, fullName: undefined }));
                }
              }}
              error={!!errors.fullName}
              helperText={errors.fullName || t('preferences.fullNameHelper')}
              margin="normal"
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />

            <FormControl fullWidth margin="normal" required error={!!errors.preferredLanguage}>
              <InputLabel>{t('registration.languageLabel')}</InputLabel>
              <Select
                value={accountForm.preferredLanguage}
                label={t('registration.languageLabel')}
                onChange={(e) => {
                  setAccountForm(prev => ({ ...prev, preferredLanguage: e.target.value }));
                  if (errors.preferredLanguage) {
                    setErrors(prev => ({ ...prev, preferredLanguage: undefined }));
                  }
                }}
              >
                <MenuItem value="es">{t('languages.es')}</MenuItem>
                <MenuItem value="en">{t('languages.en')}</MenuItem>
              </Select>
              {errors.preferredLanguage && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {errors.preferredLanguage}
                </Typography>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={accountForm.notificationsEnabled}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, notificationsEnabled: e.target.checked }))}
                />
              }
              label={t('preferences.emailNotifications')}
              sx={{ mt: 2, mb: 2 }}
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                disabled={isSaving}
                sx={{ minWidth: 120 }}
              >
                {isSaving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {t('messages.saving')}
                  </>
                ) : (
                  t('preferences.save')
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Account Deletion Section */}
        <Card sx={{ mt: 3, borderColor: 'error.main' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="error">
              {t('preferences.dangerZone')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('preferences.deleteAccountDescription')}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting}
            >
              {t('preferences.deleteAccount')}
            </Button>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle color="error">
            {t('preferences.confirmDeleteTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('preferences.confirmDeleteMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteAccount} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('messages.deleting')}
                </>
              ) : (
                t('preferences.deleteAccount')
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AccountPreferences;